import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Box, Grid, Typography, Card, Button, IconButton, Chip, Skeleton, Avatar, TextField, Link as MuiLink } from '@mui/material';
import { Facebook, Instagram, X, LinkedIn, YouTube } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header1 from '../Header/Header1';
import Footer from '../Footer/Footer';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ReportIcon from '@mui/icons-material/Report';
import ShareButton from "../Share/ShareButton";
import Footer2 from '../Footer/Footer2';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RequestRecommendations from '../Recommendations/RequestRecommendations';
import avatar from './default-avatar.png'
import ShareIcon from '@mui/icons-material/Share';
import FlagIcon from '@mui/icons-material/Flag';
import { styled } from '@mui/system';
import GoogleMeetLogo from '../Post/GoogleMeetImage.png';
import ZoomMeetLogo from '../Post/ZoomMeetImage.png';
import TeamsMeetLogo from '../Post/TeamsMeetImage.png';
import YouTubeEmbed from '../UserProfile/YouTubeEmbed';
import UserFeedbacks from '../User/UserFeedbacks';
import ViewsLikesAnalytics from '../Post/ViewsLikesAnalytics';
import ViewsLikesAnalyticsRequest from './ViewsLikesAnalyticsRequest';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';




const API_URL = process.env.REACT_APP_API_URL;


const RequestTabDetails = ({ onLogout }) => {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);


  const { id, userId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [action, setAction] = useState(null);
  const location = useLocation();
  const [bookingCreated, setBookingCreated] = useState(false)
  const paymentStatus = location.state?.paymentStatus || 'unknown';  // Default to 'unknown' if not provided
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [isValid, setIsValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [platform, setPlatform] = useState("");



  const [isVerified, setIsVerified] = useState(false);
  const [loading, setLoading] = useState(true); // to track check status

  useEffect(() => {
    const checkVerification = async () => {
      if (!userId) return;

      try {
        const response = await axios.post(`${API_URL}/api/check-verification/`, {
          user_id: userId,
        });

        setIsVerified(response.data.status === 'approved');
      } catch (error) {
        console.error('Verification check failed:', error);
        setIsVerified(false);
      } finally {
        setLoading(false);
      }
    };

    checkVerification();
  }, [userId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/get_requested_data/?request_id=${id}`);
        setCourse(response.data[0]);
        setPlatform(response.data[0].meeting_platform)
        await createRecentActivity(userId, id);
        UpdatePostViews();
        updateRecentViewedPost(userId, id);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [id, userId]);  // Depend on 'id' and 'userId' to ensure the data and view count is fetched only once



  const getLogo = (platform) => {
    switch (platform) {
      case 'googleMeet':
        return GoogleMeetLogo;
      case 'zoom':
        return ZoomMeetLogo;
      case 'microsoftTeams':
        return TeamsMeetLogo;
      default:
        return null; // Optionally, you can return a default image or a placeholder here
    }
  };


  const getPlatformName = (platform) => {
    switch (platform) {
      case 'googleMeet':
        return 'Google Meet';
      case 'zoom':
        return 'Zoom';
      case 'microsoftTeams':
        return 'Microsoft Teams';
      default:
        return null;
    }
  };

  const validateMeetingLink = (link, platform) => {
    const googleMeetRegex = /https:\/\/meet.google.com\/[a-zA-Z0-9\-]+/;

    const zoomRegex = /https:\/\/\w+\.zoom.us\/j\/\d+(\?pwd=[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]*)?/;  // Updated regex

    const teamsRegex = /https:\/\/teams\.live\.com\/meet\/[a-zA-Z0-9\-]+/;

    switch (platform) {
      case "googleMeet":
        return googleMeetRegex.test(link);
      case "zoom":
        return zoomRegex.test(link);
      case "microsoftTeams":
        return teamsRegex.test(link);
      default:
        return false;
    }
  };

  const handleLinkChange = (e) => {
    const link = e.target.value;
    setMeetingLink(link);

    if (link.trim() === "") {
      setIsValid(true); // Set valid state as true when the link is empty
      setErrorMessage(""); // Reset the error message
    } else if (platform) {
      const isLinkValid = validateMeetingLink(link, platform);
      setIsValid(isLinkValid);
      if (!isLinkValid) {
        setErrorMessage("Invalid link for the selected platform.");
      } else {
        setErrorMessage("");
      }
    }
  };


  useEffect(() => {
    const getUserPostInteraction = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/get_user_post_interaction/`,
          {
            params: {
              user_id: userId,
              post_id: id,
            },
          }
        );
        setAction(response.data.action); // Set the action (like, dislike, or null)
      } catch (error) {
        setAction(null);
        console.error("Error fetching user post interaction:", error);
      }
    };

    getUserPostInteraction();
  }, [userId, id]);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };


  const createUserPostBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/create_user_request_booking/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          request_id: id,
          user_id: userId,
          meeting_link: meetingLink,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setBookingCreated(true);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
    }
  };

  const updateRecentViewedPost = async (userId, postId) => {
    try {
      const response = await axios.patch(`${API_URL}/update-recent-viewed-post/`, {
        user_id: userId,
        post_id: postId,
      });

    } catch (error) {
      console.error('Error updating recent viewed post:', error.response ? error.response.data : error.message);
    }
  };

  const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(40),
    height: theme.spacing(40),
    borderRadius: '50%',
    border: `4px solid ${theme.palette.primary.main}`,
    marginRight: theme.spacing(4),
    borderColor: '#5555',
    "&:hover": {
      transform: "translateY(-3px)",
      borderColor: '#1E90FF',
      boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
    },
  }));



  const UpdatePostViews = async () => {
    try {
      const response = await axios.post(`${API_URL}/update_request_views/?request_id=${id}`);
    } catch (error) {
      console.error('Error updating request views:', error);
    }
  };

  const createRecentActivity = async (userId, id) => {
    try {
      const response = await fetch(`${API_URL}/create_recent_activity_request/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          post_id: id,
        }),
      });

      const data = await response.json();
      if (response.ok) {
      } else {
        console.error('Error logging recent activity:', data);
      }
    } catch (error) {
      console.error('Error calling create_recent_activity API:', error);
    }
  };

  if (!course) {
    return <Typography variant="h6" sx={{ textAlign: "center", padding: "2rem" }}>Loading...</Typography>;
  }

  const formatStartTime = (time) => {
    const [hours, minutes] = time.split(':');
    const dateObj = new Date();
    dateObj.setHours(hours);
    dateObj.setMinutes(minutes);

    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });

    return formatter.format(dateObj); // returns time in AM/PM format
  }

  const formatDuration = (durationInMinutes) => {
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;

    // If duration is less than an hour, only show minutes
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }

    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  const formatExperience = (experience) => {
    // If experience exists and is in the format HH:MM
    if (experience) {
      let minutes = experience.split(':')[0];  // Extract the minute value before ":"
      return `${minutes} min`;
    } else {
      return "New user";  // If no experience, return "New user"
    }
  }


  const {
    topic_name, section_name, subsection_name, description, language, date, start_time,
    duration, name, surname, profile_picture,
    about, rating, experience, id: cardId, price, user_id,
    facebook, instagram, x, linkedin, youtube, likes, dislikes, specialized_fields, roles, meeting_platform,
  } = course;



  // const durationFormatted = duration ? `${Math.floor(duration / 60)} hours ${duration % 60} minutes` : "N/A";
  const durationFormatted = formatDuration(duration)
  const teacherRating = rating || "Not rated yet";
  const formattedStartTime = formatStartTime(start_time);
  const formatedExperience = formatExperience(experience)

  const UpdatePostLikes = async (post_id) => {
    try {
      const response = await axios.post(`${API_URL}/update_request_likes/?request_id=${post_id}&user_id=${userId}`);
      setCourse(prevCourse => ({
        ...prevCourse,
        likes: response.data.likes,
        dislikes: response.data.dislikes
      }));
    } catch (error) {
      console.error('Error updating post views:', error);
    }
  };

  const handleLikeClick = (post_id) => {
    // Handle like click (send like data to the server or update state)
    if (action === 'like') {
      return; // Prevent double-clicking the same action
    }

    UpdatePostLikes(post_id)
    setAction('like');
  };

  const UpdatePostDisLikes = async (post_id) => {
    try {
      const response = await axios.post(`${API_URL}/update_request_dislikes/?request_id=${post_id}&user_id=${userId}`);
      setCourse(prevCourse => ({
        ...prevCourse,
        likes: response.data.likes,
        dislikes: response.data.dislikes
      }));
    } catch (error) {
      console.error('Error updating post views:', error);
    }
  };


  const handleDisLikeClick = (post_id) => {
    // Handle like click (send like data to the server or update state)
    if (action === 'dislike') {
      return; // Prevent double-clicking the same action
    }

    UpdatePostDisLikes(post_id)
    setAction('dislike');
  };


  const handleShareClick = () => {
    // Handle share click (open sharing options or logic)
  };

  const ReportPosts = async (post_id) => {
    try {
      const response = await axios.post(`${API_URL}/report_posts/?post_id=${post_id}&user_id=${userId}`);

    } catch (error) {
      console.error('Error updating post views:', error);
    }
  };

  const handleReportClick = (post_id) => {
    // Show confirmation dialog before proceeding with report
    const isConfirmed = window.confirm("Are you sure you want to report this post?");

    if (isConfirmed) {
      // Proceed with the report action if confirmed
      ReportPosts(post_id);
    } else {
      // Optionally handle the cancel action here if needed (e.g., logging or other behavior)
    }
  };


  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  return (
    <div>
      <Header1 userId={userId} onLogout={onLogout} />


      <Typography
        variant="h3"
        align="center"
        component="h1"
        fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
          mt: 10,
          letterSpacing: 7,
          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
          color: 'transparent',  // Makes the text itself transparent to show the gradient
          // fontWeight: 'bold',  // Optional: you can make the text bold
        }}
        gutterBottom
      >
        Accept Session Request
      </Typography>
      <Box sx={{ backgroundColor: "#000000", minHeight: "100vh", marginTop: "50px", marginBottom: "100px" }}>
        <Grid container spacing={3} sx={{ height: "100%", backgroundColor: '#000000', justifyContent: 'center' }}>


          <Grid container justifyContent="center">
            <Box sx={{
              padding: { xs: "2rem", sm: "3rem" }, // Responsive padding
              backgroundColor: "#2222", // Darker background for modern look
              color: "#fff",
              borderRadius: "16px",
              height: "100%",
              boxSizing: "border-box",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
              },
            }}>



              <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: "1rem" }}>
                <Avatar sx={{
                  width: 150,
                  height: 150,
                  // mr: 2,
                }} src={`${API_URL}${profile_picture}` || '/default-avatar.png'} alt={name || 'User'} />






              </Box>


              <Box sx={{ textAlign: 'center', marginTop: '0rem', padding: '1rem', width: '100%' }}>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "#fff",
                    letterSpacing: 7,
                    fontFamily: "'Helvetica', sans-serif",
                    "&:hover": { color: '#1E90FF' },
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },  // Responsive font size
                  }}
                >
                  {name || "name"} {surname || "surname"}
                </Typography>






              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  marginBottom: "1.5rem",
                  color: "#fff",
                  letterSpacing: 1,
                  textAlign: "center",
                  fontFamily: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
                  fontSize: {
                    xs: '1rem',   // small phones
                    sm: '1.2rem', // tablets
                    md: '1.4rem', // small desktops
                    lg: '1.55rem' // large desktops
                  },
                  lineHeight: 1.6,
                  "&:hover": {
                    color: '#1E90FF',
                  },
                }}
              >
                "{description}"
              </Typography>

              {/* Grid of details */}
              <Grid container spacing={3} justifyContent="center" sx={{ mt: 0 }}>

                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: "#fff",
                    background: "linear-gradient(45deg, #333333, #555555)",

                    borderRadius: "8px",
                    padding: "6px 12px",
                    mr: 1,
                    mt: 1,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                    display: "inline-block",
                    fontSize: "0.875rem", // Smaller size for compactness
                    letterSpacing: "0.8px",
                    transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      background: "linear-gradient(45deg, #6e7dff, #6b89ff)",
                      transform: "scale(1.05)",
                    },
                    "@media (max-width: 600px)": {
                      fontSize: "0.75rem", // Make text smaller on mobile
                      padding: "4px 10px", // Adjust padding for smaller screens
                    },
                  }}
                >
                  Language: {language || "N/A"}
                </Typography>


                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: "#fff",
                    background: "linear-gradient(45deg, #333333, #555555)",
                    borderRadius: "8px",
                    padding: "6px 12px",
                    textTransform: "uppercase",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                    display: "inline-block",
                    mr: 1,
                    mt: 1,
                    letterSpacing: 2,
                    fontSize: "0.875rem", // Smaller size for compactness
                    letterSpacing: "0.8px",
                    transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      background: "linear-gradient(45deg, #6e7dff, #6b89ff)",
                      transform: "scale(1.05)",
                    },
                    "@media (max-width: 600px)": {
                      fontSize: "0.75rem", // Make text smaller on mobile
                      padding: "4px 10px", // Adjust padding for smaller screens
                    },
                  }}
                >
                  Price: {`\u20B9`}{price}
                </Typography>





                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: "#fff",
                    background: "linear-gradient(45deg, #333333, #555555)",

                    borderRadius: "8px",
                    padding: "6px 12px",
                    textTransform: "uppercase",
                    mr: 1,
                    mt: 1,
                    letterSpacing: 2,
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                    display: "inline-block",
                    fontSize: "0.875rem", // Smaller size for compactness
                    letterSpacing: "0.8px",
                    transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      background: "linear-gradient(45deg, #6e7dff, #6b89ff)",
                      transform: "scale(1.05)",
                    },
                    "@media (max-width: 600px)": {
                      fontSize: "0.75rem", // Make text smaller on mobile
                      padding: "4px 10px", // Adjust padding for smaller screens
                    },
                  }}
                >
                  Date: {date ? new Date(date).toLocaleDateString('en-GB') : 'N/A'}
                </Typography>


                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: "#fff",
                    background: "linear-gradient(45deg, #333333, #555555)",

                    borderRadius: "8px",
                    padding: "6px 12px",
                    textTransform: "uppercase",
                    mr: 1,
                    mt: 1,
                    letterSpacing: 2,
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                    display: "inline-block",
                    fontSize: "0.875rem", // Smaller size for compactness
                    letterSpacing: "0.8px",
                    transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      background: "linear-gradient(45deg, #6e7dff, #6b89ff)",
                      transform: "scale(1.05)",
                    },
                    "@media (max-width: 600px)": {
                      fontSize: "0.75rem", // Make text smaller on mobile
                      padding: "4px 10px", // Adjust padding for smaller screens
                    },
                  }}
                >
                  Start Time: {formattedStartTime || "N/A"}
                </Typography>


                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: "#fff",
                    background: "linear-gradient(45deg, #333333, #555555)",

                    borderRadius: "8px",
                    padding: "6px 12px",
                    textTransform: "uppercase",
                    mr: 1,
                    mt: 1,
                    letterSpacing: 2,
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)",
                    display: "inline-block",
                    fontSize: "0.875rem", // Smaller size for compactness
                    letterSpacing: "0.8px",
                    transition: "transform 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      background: "linear-gradient(45deg,  #6e7dff, #6b89ff)",
                      transform: "scale(1.05)",
                    },
                    "@media (max-width: 600px)": {
                      fontSize: "0.75rem", // Make text smaller on mobile
                      padding: "4px 10px", // Adjust padding for smaller screens
                    },
                  }}
                >
                  Duration: {durationFormatted}
                </Typography>



              </Grid>

              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                sx={{
                  padding: { xs: '16px', sm: '24px', md: '32px' }, // responsive padding
                  textAlign: 'center',
                  width: '100%', // ensures box takes full width
                }}
              >
                {getLogo(meeting_platform) ? (
                  <img
                    src={getLogo(meeting_platform)}
                    alt={meeting_platform}
                    style={{
                      width: '80px', // default width for small screens
                      height: 'auto', // preserve aspect ratio
                      maxWidth: '100px', // maximum width for larger screens
                    }}
                  />
                ) : (
                  <span style={{ fontStyle: 'italic', color: 'gray', letterSpacing: 4, }}>
                    Meeting Platform Info Not available
                  </span>
                )}

                <Typography
                  variant="body1"
                  sx={{
                    marginTop: '12px',
                    fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
                    fontWeight: 500,
                    letterSpacing: 5,
                    color: '#333',
                  }}
                >
                  {getPlatformName(meeting_platform)} {/* Call getPlatformName() properly */}
                </Typography>
              </Box>

              <TextField
                label="Paste Session Link"
                variant="outlined"
                fullWidth
                sx={{ letterSpacing: 2 }}
                value={meetingLink}
                onChange={handleLinkChange}
                error={isValid === false}
                helperText={
                  isValid === false && meetingLink.trim() !== ""
                    ? errorMessage
                    : "Please go to above displayed platform, generate the link for session, and paste it here."
                }
              />


              {isValid === true && meetingLink.trim() !== "" && <p style={{ color: "green", letterSpacing: 2 }}>Link is valid!</p>}
              {isValid === false && meetingLink.trim() !== "" && <p style={{ color: "red", letterSpacing: 2 }}>Invalid link for the displayed platform.</p>}

              {(paymentStatus === 'success' || bookingCreated) ? (
                <Box
                  sx={{
                    textAlign: "center",
                    mt: 5,
                    px: 3,
                    py: 4,
                    borderRadius: "12px",
                    backgroundColor: "#001F3F", // Subtle dark blue background
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)", // Soft shadow for depth
                  }}
                >
                  <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 40, mb: 2 }} />

                  <Typography
                    variant="h6"
                    sx={{
                      color: "#1E90FF",
                      letterSpacing: 2,
                      fontWeight: 700,
                      fontSize: {
                        xs: "1.1rem",
                        sm: "1.3rem",
                        md: "1.5rem",
                      },
                      lineHeight: 1.6,
                      mb: 1,
                    }}
                  >
                    Request Accepted Successfully!
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: "#b0c4de",
                      letterSpacing: 1.5,
                      fontSize: {
                        xs: "0.9rem",
                        sm: "1rem",
                        md: "1.1rem",
                      },
                      maxWidth: "600px",
                      margin: "0 auto",
                    }}
                  >
                    Please visit{' '}
                    <MuiLink
                      component={Link}
                      to={`/accepted-request-tab-details/${id}/${userId}`}
                      underline="hover"
                      sx={{ color: "#ffffff", fontWeight: "bold" }}
                    >
                      Accepted
                    </MuiLink>{' '}
                    to view your updates.
                  </Typography>
                </Box>
              ) : paymentStatus === 'cancelled' ? (
                <>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#e74c3c", // Brighter red for better attention-grabbing
                      textAlign: "center",
                      mt: 3,
                      letterSpacing: 7,
                      fontWeight: "600", // Slightly bolder for emphasis
                      fontSize: {
                        xs: "1.1rem", // Adjust font size for small screens
                        sm: "1.3rem", // Slightly larger for medium screens
                        md: "1.5rem", // Larger font for desktop
                      },
                      lineHeight: 1.6, // Improved line height for readability
                      background: "rgba(231, 76, 60, 0.1)", // Light background color for contrast
                      borderRadius: "8px", // Rounded corners for a modern look
                      padding: "0.8rem 1.5rem", // Extra padding for a more balanced layout
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Soft shadow to make the message stand out
                      transition: "all 0.3s ease", // Smooth transition for interactive effect
                      "&:hover": {
                        transform: "scale(1.05)", // Slight zoom effect on hover for interactivity
                        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)", // Larger shadow on hover
                      },
                    }}
                  >
                    Payment was cancelled. Please try again.
                  </Typography>

                  <Button
                    component={Link}
                    to={`/payments/post/${id}`}
                    variant="contained"
                    fullWidth
                    sx={{
                      marginTop: "2rem",
                      backgroundColor: "#2980b9",
                      color: "#fff",
                      letterSpacing: 7,
                      textTransform: "none",
                      padding: "1rem",
                      fontWeight: "bold",
                    }}
                  >
                    Retry Payment
                  </Button>
                </>
              ) : (
                <>


                  <Button

                    variant="contained"
                    fullWidth
                    disabled={!isVerified || loading}
                    sx={{
                      marginTop: "2rem",
                      backgroundColor: isVerified ? '#1976d2' : '#aaa',
                      color: "#fff",
                      textTransform: "none",
                      padding: "1rem 2rem", // Add horizontal padding for better appearance
                      fontWeight: "bold",
                      letterSpacing: 5,

                      fontSize: {
                        xs: "1rem", // Smaller size for mobile
                        sm: "1.1rem", // Medium size for tablets and small screens
                        md: "1.2rem", // Larger size for desktops
                      },
                      borderRadius: "50px", // Rounded, pill-shaped button for a modern look
                      boxShadow: "0 12px 18px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                      textAlign: "center",
                      transition: "background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease", // Smooth transitions for hover effects
                      "&:hover": {
                        background: "linear-gradient(45deg, #1e90ff, #1c70ff)", // Slightly darker gradient on hover
                        transform: "scale(1.05)", // Slight zoom effect on hover for interactivity
                        boxShadow: "0 18px 30px rgba(0, 0, 0, 0.2)", // Stronger shadow on hover for more depth
                      },
                      "@media (max-width: 600px)": {
                        padding: "0.8rem 1.5rem", // Adjust padding for small screens
                      },
                    }}
                    onClick={() => {
                      setOpenConfirmDialog(true); // Open the dialog
                    }}

                  >
                    Accept Now
                  </Button>
                  {!loading && !isVerified && (
                    <Typography
                      variant="body2"
                      color="#1E90FF"
                      sx={{ mt: 2, letterSpacing: 4, textAlign: 'center' }}
                    >
                      Only verified users can accept requests
                    </Typography>
                  )}



                </>
              )}

              <Grid container spacing={2} sx={{
                justifyContent: "center", marginTop: "5px", backgroundColor: '#1111', borderColor: '#5555',
                borderRadius: "15px",
                "&:hover": {
                  borderColor: '#1E90FF',
                },
              }}>


                <Grid item xs={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Link component="button" onClick={() => handleReportClick(id)} sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* <ReportIcon sx={{ fontSize: "1.8em", color: "white" }} /> */}

                    <Box display="flex" flexDirection="column" alignItems="center">
                      <FlagIcon
                        sx={{
                          fontSize: { xs: "3rem", sm: "3rem", md: "3rem" }, // Responsive icon size for different screens
                          color: "white",
                          transition: "all 0.3s ease", // Smooth transitions for hover effects
                          cursor: "pointer", // Pointer cursor to indicate interactivity
                          "&:hover": {
                            color: "#e74c3c", // Change color on hover (using a red shade for attention)
                            transform: "scale(1.1)", // Slight zoom effect for interactivity
                          },
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.2)", // Add subtle shadow for depth
                          borderRadius: "8px", // Smooth rounded corners for a modern look
                          padding: "8px", // Padding to give space around the icon
                          display: "inline-block", // Ensure it behaves like an inline element
                        }}
                      />



                    </Box>


                  </Link>
                </Grid>


              </Grid>




            </Box>

          </Grid>

          <Dialog
            open={openConfirmDialog}
            onClose={() => setOpenConfirmDialog(false)}
            PaperProps={{
              sx: {
                borderRadius: "16px",
                padding: 2,
                backgroundColor: "#000000",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.6)",
                maxWidth: 420,
                mx: 'auto',
              },
            }}
            transitionDuration={300}
          >
            <DialogTitle
              sx={{
                fontWeight: 'bold',
                fontSize: '1.5rem',
                textAlign: 'center',
                color: '#1E90FF',
                pb: 1,
              }}
            >
              Confirm Session
            </DialogTitle>

            <DialogContent sx={{ textAlign: 'center', px: 3 }}>
              <Typography sx={{ fontSize: '1rem', color: '#ccc', mb: 2 }}>
                Are you sure you want to accept this session request?
              </Typography>

              <TextField
                label="Paste Session Link"
                variant="outlined"
                fullWidth
                sx={{ letterSpacing: 2 }}
                value={meetingLink}
                onChange={handleLinkChange}
                error={isValid === false}
                helperText={
                  isValid === false && meetingLink.trim() !== ""
                    ? errorMessage
                    : "Please go to above displayed platform, generate the link for session, and paste it here."
                }
              />


              {isValid === true && meetingLink.trim() !== "" && <p style={{ color: "green", letterSpacing: 2 }}>Link is valid!</p>}
              {isValid === false && meetingLink.trim() !== "" && <p style={{ color: "red", letterSpacing: 2 }}>Invalid link for the displayed platform.</p>}

              {!meetingLink && (
                <Typography sx={{ color: 'orange', fontSize: '0.875rem', mt: 1 }}>
                  Please paste the meeting link to proceed.
                </Typography>
              )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button
                onClick={() => setOpenConfirmDialog(false)}
                sx={{
                  borderRadius: '25px',
                  padding: '0.6rem 1.5rem',
                  backgroundColor: '#333',
                  color: '#ddd',
                  textTransform: 'none',
                  "&:hover": {
                    backgroundColor: '#444',
                  },
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={() => {
                  createUserPostBookings(meetingLink); // Pass meetingLink to the function
                  setOpenConfirmDialog(false);
                }}
                variant="contained"
                disabled={!meetingLink}
                sx={{
                  borderRadius: '25px',
                  padding: '0.6rem 1.8rem',
                  textTransform: 'none',
                  background: "linear-gradient(45deg, #1e90ff, #1c70ff)",
                  fontWeight: 'bold',
                  letterSpacing: 1,
                  color: '#fff',
                  opacity: meetingLink ? 1 : 0.6,
                  "&:hover": {
                    transform: meetingLink ? 'scale(1.05)' : 'none',
                    boxShadow: meetingLink
                      ? '0 8px 20px rgba(30, 144, 255, 0.4)'
                      : 'none',
                  },
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>


        </Grid>
        <UserFeedbacks userId={user_id} />
      </Box>
      <IconButton
        onClick={scrollToTop}
        sx={{ position: 'fixed', bottom: 16, right: 16, backgroundColor: 'white', color: 'black' }}>
        <ArrowUpwardIcon />
      </IconButton>
      <Footer2 />
    </div>
  );
};

export default RequestTabDetails;